import { useState } from 'react';

import { useApiRequest } from '~/hooks/api';
import api from '~/services/api';
import { IClientAllocationDetailed } from '~/services/api/clientAllocation/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { IOption } from '~/types';
import {
  clientAllocationMapper,
  clientMapper,
  ITeamMemberMapped,
  sortByName,
  teamMemberMapper,
} from '~/utils/clientAllocation';
import extractFullName from '~/utils/text/extractFullName';
import AllocationTypes from '../constants/allocationTypes';

interface UseGetEditClientAllocationsParams {
  actTeamId: string;
  clientAllocationDate: string;
  isCrisis: boolean;
}

interface UseGetEditClientAllocationsResult {
  isLoading: boolean;
  teamMembers: ITeamMemberMapped[];
  clients: IOption[];
  assignedClients: number[];
  refetch: () => Promise<void>;
}

const useGetEditClientAllocations = ({
  actTeamId,
  clientAllocationDate,
  isCrisis,
}: UseGetEditClientAllocationsParams): UseGetEditClientAllocationsResult => {
  const [teamMembers, setTeamMembers] = useState<ITeamMemberMapped[]>([]);
  const [clients, setClients] = useState<IOption[]>([]);
  const [assignedClients, setAssignedClients] = useState<number[]>([]);

  const {
    clinic: userClinic,
    id: userId,
    photo,
    ...user
  } = useStoreState(state => state.user.current);
  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const { onGetClinicActTeam, onGetActTeamsAvailable } = useStoreActions(
    actions => actions.actTeam,
  );
  const {
    onGetClientAllocation,
    onGetOwnClientAllocation,
    onGetOwnClientAllocationGU,
    onGetAllocationClientsByGroupGU,
    onGetAllocationClientsByGroup,
  } = useStoreActions(actions => actions.clientAllocation);

  const { isActTeamMember, isGlobalUser } = useRole();
  const userFullName = extractFullName(user);

  const { isLoading, refetch } = useApiRequest(
    async () => {
      const clinicId = String(userClinic.id);
      const allocationDate = `${clientAllocationDate}T00:00:00Z`;

      // Step 1: Fetch clinic and team data
      await onGetMyClinic();
      await (isGlobalUser
        ? onGetActTeamsAvailable({ clinicId })
        : onGetClinicActTeam({ clinicId, actTeamId }));

      // Step 2: Load team members and clients (parallel for non-global users)
      if (isGlobalUser) {
        setTeamMembers([{ id: userId, fullName: userFullName, photo, isDeleted: false }]);
      } else {
        const [clientsResponse, teamMembersResponse] = await Promise.all([
          api.clientAllocation.getRelatedClients(clinicId, actTeamId),
          api.actTeamMember.getActTeamMemberList(clinicId, actTeamId),
        ]);

        setClients(clientsResponse.data.map(clientMapper));

        const mappedTeamMembers = teamMembersResponse.data.sort(sortByName).map(teamMemberMapper);
        const filteredMembers =
          isActTeamMember || isGlobalUser
            ? mappedTeamMembers.filter(member => member.id === userId)
            : mappedTeamMembers;
        setTeamMembers(filteredMembers);
      }

      // Step 3: Fetch client allocations
      if (isGlobalUser) {
        await onGetOwnClientAllocationGU({
          clientAllocationDate: allocationDate,
          allocationType: isCrisis ? AllocationTypes.CRISIS : AllocationTypes.STANDARD,
        });
      } else if (isActTeamMember) {
        await onGetOwnClientAllocation({
          clientAllocationDate: allocationDate,
          allocationType: isCrisis ? AllocationTypes.CRISIS : AllocationTypes.STANDARD,
        });
      } else {
        const allocations: IClientAllocationDetailed[] = await onGetClientAllocation({
          clinicId,
          teamId: actTeamId,
          clientAllocationDate: allocationDate,
          allocationType: isCrisis ? AllocationTypes.CRISIS : AllocationTypes.STANDARD,
        });
        setAssignedClients(clientAllocationMapper(allocations));
      }

      // Step 4: Fetch allocation clients by group
      await (isGlobalUser
        ? onGetAllocationClientsByGroupGU({ clinicId })
        : onGetAllocationClientsByGroup({ clinicId, teamId: actTeamId }));
    },
    {
      dependencies: [
        actTeamId,
        clientAllocationDate,
        isActTeamMember,
        isGlobalUser,
        userClinic.id,
        userId,
        userFullName,
        photo,
      ],
    },
  );

  return {
    isLoading,
    teamMembers,
    clients,
    assignedClients,
    refetch,
  };
};

export default useGetEditClientAllocations;
